'use client';
import { type Brand } from '@curology/ui-components-web/brand';
import { Button, Typography } from '@curology/ui-components-web/react';
import React, { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { PriceSummary } from '@/components/flow/expressCurologySignUpSkin/components/priceSummary';
import {
  type AddOnProductType,
  type ExpressCurologySignUpSkinPdpSourceConfigType,
} from '@/components/flow/expressCurologySignUpSkin/types';
import { useRouter } from '@/hooks/useRouter';
import { GuestService } from '@/services/guestService/client';
import { OffersService } from '@/services/offersService/client';
import { editSessionBag, getVisit } from '@/services/visitsService/client';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';

import { AddOnProductCard } from './components/addOnProductCard';
import { HowItWorksAndNextBoxPricing } from './components/howItWorksAndNextBoxPricing';
import { useNavigation } from '../../hooks/useNavigation';

export const ClientContainer = ({
  brand,
  bundleConfig,
  giftWithPurchaseImageUrl,
  uuid,
}: {
  brand: Brand;
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType;
  giftWithPurchaseImageUrl?: string;
  uuid: string;
}) => {
  const [selectedAddOnProductItemIds, setSelectedAddOnProductItemIds] =
    useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const router = useRouter();
  const flashMessages = useFlashMessages();
  const { onNext } = useNavigation();

  const { addOnProducts, offerId } = bundleConfig;

  const addAddOnProduct = (addOnProduct: AddOnProductType) => {
    setSelectedAddOnProductItemIds([
      ...selectedAddOnProductItemIds,
      ...addOnProduct.itemIds,
    ]);

    trackEvent(AnalyticsEvent.PRODUCT_ADDED, {
      flow: FlowTypes.PDP_CHECKOUT,
      offerId,
      productName: addOnProduct.name,
    });
  };

  const removeAddOnProduct = (addOnProduct: AddOnProductType) => {
    setSelectedAddOnProductItemIds(
      selectedAddOnProductItemIds.filter(
        (selectedAddOnProductItemId) =>
          !addOnProduct.itemIds.includes(selectedAddOnProductItemId),
      ),
    );

    trackEvent(AnalyticsEvent.PRODUCT_REMOVED, {
      flow: FlowTypes.PDP_CHECKOUT,
      offerId,
      productName: addOnProduct.name,
    });
  };

  const submitPage = async () => {
    setIsSubmitting(true);

    try {
      await editSessionBag({
        brand,
        sessionBag: {
          hasViewedBundle: true,
          selectedAddOnProductItemIds,
        },
        uuid,
      });

      trackEvent(AnalyticsEvent.PROCEED_TO_CHECKOUT_BUTTON_CLICKED, {
        flow: FlowTypes.PDP_CHECKOUT,
      });

      onNext();
    } catch (e: any) {
      setIsSubmitting(false);

      flashMessages.error({
        title: 'Sorry!',
        message: 'A system error occurred. Please try again.',
      });
    }
  };

  useEffect(() => {
    const getCurrentVisit = async () => {
      const visit = await getVisit({ uuid });

      if (visit?.sessionBag?.selectedAddOnProductItemIds) {
        setSelectedAddOnProductItemIds(
          visit.sessionBag.selectedAddOnProductItemIds,
        );
      }

      if (visit?.sessionBag?.hasActivatedTrial) {
        router.refresh();
      }
    };

    const runCookieMiddleware = async () => {
      await GuestService.runCookieMiddleware(brand);
    };

    getCurrentVisit();
    runCookieMiddleware();
    OffersService.updateUserOffer(offerId, brand);
  }, [brand, offerId, uuid, router]);

  return (
    <React.Fragment>
      <Typography size="h2">Get your personalized Rx treatment</Typography>
      <Typography>
        Target your skin goals with a simple routine designed to work for you.
      </Typography>
      <PriceSummary
        bundleConfig={bundleConfig}
        giftWithPurchaseImageUrl={giftWithPurchaseImageUrl}
        selectedAddOnProductItemIds={selectedAddOnProductItemIds}
      />
      <Typography className="cur-bg-surface-highlight cur-p-4 cur-text-brand">
        We&apos;ll personalize your Rx treatment after checkout.
      </Typography>
      {addOnProducts && (
        <React.Fragment>
          <Typography size="h2">
            Add routine essentials—FREE with your 30-day trial
          </Typography>
          <ul className="cur-flex cur-flex-wrap cur-mb-0 -cur-mr-4">
            {addOnProducts.map((addOnProduct) => {
              return (
                <AddOnProductCard
                  addAddOnProduct={addAddOnProduct}
                  addOnProduct={addOnProduct}
                  isSubmitting={isSubmitting}
                  key={addOnProduct.id}
                  offerId={offerId}
                  removeAddOnProduct={removeAddOnProduct}
                  selectedAddOnProductItemIds={selectedAddOnProductItemIds}
                />
              );
            })}
          </ul>
        </React.Fragment>
      )}
      <HowItWorksAndNextBoxPricing
        bundleConfig={bundleConfig}
        selectedAddOnProductItemIds={selectedAddOnProductItemIds}
      />
      <div className="cur-bg-[#f5f2ec] cur-bottom-0 cur-py-4 cur-sticky">
        <Button
          className="cur-w-full"
          disabled={isSubmitting}
          onClick={submitPage}
          type="button"
        >
          Check out now
        </Button>
      </div>
    </React.Fragment>
  );
};
